// Scope handles that re-open themselves (BUG-2065, ADR 066 — Offline Mode § 5.1): when the web
// view drops the store's connection, a call fails with OfflineStoreClosedError; these handles open
// the scope again through the gateway and retry that call once, so a long-lived holder (the sync
// engine, the change queue, offline writes) recovers without a relaunch.
import { OfflineStoreClosedError } from './backend';
import type {
  OfflineLotStates,
  OfflineMediaStaging,
  OfflineOutbox,
  OfflineRecords,
  OfflineStore,
  OfflineThumbnails,
} from './types';

/** The method groups a scope's handles carry. */
type ScopeFacet = OfflineRecords | OfflineThumbnails | OfflineOutbox | OfflineLotStates | OfflineMediaStaging;

/** Media staging's multi-step, non-idempotent write: re-opened after, never retried. */
const MEDIA_NOT_RETRIED: ReadonlySet<string> = new Set<keyof OfflineMediaStaging>(['stage']);

/** A scope's store and, when the build stages files, its media staging. */
export interface ReopenableScopeHandles {
  store: OfflineStore;
  media: OfflineMediaStaging | null;
}

/**
 * Wrap a scope's handles so a call that fails with OfflineStoreClosedError re-opens the scope
 * and is retried once.
 *
 * The retry is safe because every retried method either writes in one transaction (a closed
 * connection refuses it whole; a wiped scope refuses before its task runs) or writes in several
 * that are idempotent (puts and deletes by key), so a retry lands the same state. Media staging's
 * `stage` is neither: it claims its row before writing the file, so a connection lost between
 * the two leaves a claim its retry would read as a duplicate id. It is never retried: the scope
 * is re-opened, then its error reaches the caller, so the next pick stages on a live connection.
 *
 * A second failure, or any failure once `isLive` is false, is thrown as it is: a holder whose
 * session has ended never opens its scope again, so it can never wipe or write into the next
 * user's store.
 *
 * @param input.initial - the handles the scope was first opened with.
 * @param input.reopen - opens the same scope again through the gateway.
 * @param input.isLive - false once the holder's session has ended.
 * @returns handles with the same scope and methods.
 */
export function createReopeningScopeHandles(input: {
  initial: ReopenableScopeHandles;
  reopen: () => Promise<ReopenableScopeHandles>;
  isLive: () => boolean;
}): ReopenableScopeHandles {
  let current = input.initial;
  let reopening: Promise<ReopenableScopeHandles> | null = null;

  // Concurrent failures on the same dead handles share one re-open.
  const reopenFrom = (failed: ReopenableScopeHandles): Promise<ReopenableScopeHandles> => {
    if (current !== failed) return Promise.resolve(current);
    reopening ??= input
      .reopen()
      .then((next) => {
        current = next;
        return next;
      })
      .finally(() => {
        reopening = null;
      });
    return reopening;
  };

  const call = async <T>(run: (handles: ReopenableScopeHandles) => Promise<T>, retry: boolean): Promise<T> => {
    const used = current;
    try {
      return await run(used);
    } catch (err) {
      if (!(err instanceof OfflineStoreClosedError) || !input.isLive()) throw err;
      const next = await reopenFrom(used);
      if (!retry || !input.isLive()) throw err;
      return run(next);
    }
  };

  // Every method of a facet, called on whichever handles are current. The facets are plain
  // objects of async methods (plus media's `scope`), so their own keys are the whole surface.
  const facet = <F extends ScopeFacet>(
    sample: F,
    pick: (handles: ReopenableScopeHandles) => F | null,
    notRetried: ReadonlySet<string> = new Set(),
  ): F => {
    const wrapped = new Map<string, unknown>();
    for (const name of Object.keys(sample) as (keyof F & string)[]) {
      if (typeof sample[name] !== 'function') continue;
      wrapped.set(name, (...args: unknown[]) =>
        call((handles) => {
          const target = pick(handles);
          // A re-open that came back without media staging (the plugin went away) cannot serve it.
          if (target == null) throw new OfflineStoreClosedError();
          return (target[name] as (...a: unknown[]) => Promise<unknown>)(...args);
        }, !notRetried.has(name)),
      );
    }
    // Built key by key from `sample`'s own methods, so it carries F's surface.
    return Object.freeze(Object.fromEntries(wrapped)) as unknown as F;
  };

  const { store, media } = input.initial;
  return {
    store: Object.freeze({
      scope: store.scope,
      records: facet(store.records, (h) => h.store.records),
      thumbnails: facet(store.thumbnails, (h) => h.store.thumbnails),
      outbox: facet(store.outbox, (h) => h.store.outbox),
      lotStates: facet(store.lotStates, (h) => h.store.lotStates),
    }),
    media:
      media == null
        ? null
        : Object.freeze({ ...facet(media, (h) => h.media, MEDIA_NOT_RETRIED), scope: media.scope }),
  };
}
